import React, { PureComponent } from 'react'

import './VolumeControl.css'
import './VolumeControlStudentView.css'

import { IconButton } from '@nearpod-packages/base-library';
import styled from 'styled-components'

const volumeValues: any = {
	MIN_VOLUME: 0,
	MAX_VOLUME: 1,
}

interface IVolumeBarState {
	showSlider: boolean
	volumeAnnouncement: string
}

const VolumeControlContainer = styled.div<{ $isTransparent?: boolean }>`
	@media (max-width: 700px) {
		align-items: center;
        display: flex;
        justify-content: center;
	}
`

interface IVolumeBarProps {
	onVolumeChange: (volume: number) => void
	isMuted: boolean
	volume: number
	onMuteSelected: (isMuted: boolean) => void
	isStudentView?: boolean
	isTransparent?: boolean
	widthSize: number
}

class VolumeBar extends PureComponent<IVolumeBarProps, IVolumeBarState> {
	constructor(props: IVolumeBarProps) {
		super(props)
		this.state = {
			showSlider: false,
			volumeAnnouncement: '',
		}
	}

	inputFactor = 100
	DESKTOP_BREAKPOINT = 800

	toggleMute = () => {
		this.props.onMuteSelected(!this.props.isMuted)
	}

	isDesktop = () => this.props.widthSize >= this.DESKTOP_BREAKPOINT
	isMobile = () => this.props.widthSize < this.DESKTOP_BREAKPOINT

	announceVolumeChange = (message: string) => {
		this.setState({ volumeAnnouncement: message })
		setTimeout(() => this.setState({ volumeAnnouncement: '' }), 150)
	}

	calculateNewVolume = (key: string, currentValue: number): number | null => {
		const step = 5
		switch (key) {
			case 'ArrowUp':
			case 'ArrowRight':
				return Math.min(currentValue + step, volumeValues.MAX_VOLUME * this.inputFactor)
			case 'ArrowDown':
			case 'ArrowLeft':
				return Math.max(currentValue - step, volumeValues.MIN_VOLUME)
			case 'Home':
				return volumeValues.MIN_VOLUME
			case 'End':
				return volumeValues.MAX_VOLUME * this.inputFactor
			default:
				return null
		}
	}

	handleButtonKeyDown = (event: React.KeyboardEvent) => {
		switch (event.key) {
			case 'Enter':
			case ' ':
				event.preventDefault()
				this.toggleMute()
				const muteState = !this.props.isMuted ? 'Muted' : `Unmuted, volume ${Math.round(this.getInputValue())}%`
				this.announceVolumeChange(muteState)
				return
			default:
				return
		}
	}

	handleButtonFocus = () => {
		this.setState({ showSlider: true })
	}

	handleButtonBlur = () => {
		this.setState({ showSlider: false })
	}

	handleSliderFocus = () => {
		this.setState({ showSlider: true })
	}

	handleSliderBlur = () => {
		this.setState({ showSlider: false })
	}

	handleSliderKeyDown = (event: React.KeyboardEvent) => {
		// Prevenir que NVDA intercepte las teclas de navegación
		if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
			event.stopPropagation()
		}

		const currentValue = this.getInputValue()
		const newValue = this.calculateNewVolume(event.key, currentValue)

		if (newValue === null) return

		event.preventDefault()
		const volume = newValue / this.inputFactor
		this.props.onVolumeChange(volume)

		if (this.props.isMuted && newValue > 0) {
			this.props.onMuteSelected(false)
		}
	}

	executeOnDevice = (callback: () => void, forDesktop = true) => {
		if ((forDesktop && this.isDesktop()) || (!forDesktop && this.isMobile())) {
			callback()
		}
	}

	toggleSlider = () => {
		this.setState(({ showSlider }) => ({ showSlider: !showSlider }))
	}

	showSlider = () => {
		this.setState({
			showSlider: true,
		})
	}

	hideSlider = () => {
		this.setState({
			showSlider: false,
		})
	}

	handleInputChange = (event: any) => {
		event.preventDefault()
		const volume = parseInt(event.target.value) / this.inputFactor
		this.props.onVolumeChange(volume)
	}

	getInputValue = () => (this.props.isMuted ? volumeValues.MIN_VOLUME : this.props.volume * this.inputFactor)

	renderVolumeControl = () => {
		return (
			<>
				<div
					className='wrapper'
					onMouseOver={() => this.executeOnDevice(this.showSlider, true)}
					onMouseLeave={() => this.executeOnDevice(this.hideSlider, true)}
					onClick={() => this.executeOnDevice(this.toggleSlider, false)}
				>
					<IconButton
						className='volume-wrapper'
						icon={this.props.isMuted ? 'volume_off' : 'volume_up'}
						size="default"
						subVariant="contained"
						variant="tertiary"
						onClick={this.toggleMute}
						onKeyDown={this.handleButtonKeyDown}
						onFocus={this.handleButtonFocus}
						onBlur={this.handleButtonBlur}
						aria-label={this.props.isMuted ? 'Unmute audio' : 'Mute audio'}
						aria-pressed={this.props.isMuted}
					/>
					<div
						className='volume-slider-trigger'
						role="slider"
						tabIndex={0}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={Math.round(this.getInputValue())}
						aria-valuetext={`Volume ${Math.round(this.getInputValue())}%`}
						aria-label="Volume"
						onFocus={this.handleSliderFocus}
						onBlur={this.handleSliderBlur}
						onKeyDown={this.handleSliderKeyDown}
					/>

					<div
						id="volume-slider"
						className={this.props.isStudentView ? 'container-student-view' : 'container-teacher-view'}
						style={{
							visibility: this.state.showSlider ? 'visible' : 'hidden',
							opacity: this.state.showSlider ? 1 : 0,
							pointerEvents: this.state.showSlider ? 'auto' : 'none'
						}}
						aria-hidden="true"
						role="presentation"
					>
							<div className={this.props.isStudentView ? 'container-slider-student-view' : 'container-slider'}>
								<input
									onChange={this.handleInputChange}
									type='range'
									min={volumeValues.MIN_VOLUME}
									max={volumeValues.MAX_VOLUME * this.inputFactor}
									value={this.getInputValue()}
									className='container-slider-input'
									tabIndex={-1}
									aria-hidden="true"
									style={{
										background: `linear-gradient(to right, #00a8ff 0%,
												#00a8ff ${this.getInputValue()}%,
												#fff ${this.getInputValue()}%, #fff 100%)`,
									}}
								/>
							</div>
						</div>
					</div>
					<div aria-live="polite" aria-atomic="true" className="sr-only">
						{this.state.volumeAnnouncement}
					</div>
			</>
		)
	}

	render() {
		return (
			<VolumeControlContainer
				$isTransparent={this.props.isTransparent}
				onMouseOver={this.props.isStudentView ? () => this.executeOnDevice(this.showSlider, true) : undefined}
			>
				{this.renderVolumeControl()}
			</VolumeControlContainer>
		)
	}
}

export default VolumeBar
